import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
  Decimation,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
  Decimation
);

// Custom Tick Plugin
const customPressureTicksPlugin = {
  id: 'customPressureTicks',
  beforeInit(chart) {
    if (
      chart.options &&
      chart.options.scales &&
      chart.options.scales.yPressure
    ) {
      chart.options.scales.yPressure.afterBuildTicks = function (axis) {
        axis.ticks = [-88.5, -80, -70, -60, -50, -40, -30, -20, -10, 0, 10, 20, 30, 40, 50, 61, 66.5];
      };
    }
  }
};

const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

const ISGraphChartjs = ({ data, deviceId, height = 500 }) => {
  const rounded = (num) => Math.round(Math.abs(num) / 5) * 5;

  let lastdept = null;

  const value = data?.[0]?.[1]?.[2];

  if (
    value &&
    ['115', '110', '135', '150'].includes(String(Math.round(value)))
  ) {
    lastdept = value;
  } else {
    lastdept = data?.[0]?.slice(-1)?.[0]?.[2] || 0;
  }

  // Rebuilt only when the readings change. Previously this object - and its
  // random colours - was recreated on every render of the page, so unrelated
  // state changes redrew the whole chart.
  const chartData = useMemo(() => ({
    datasets: (data || []).map((entry, i) => {
      const timestamp = entry[0];
      const dateStr = new Date(timestamp).toLocaleString("en-US", {
        hour12: false,
      }).slice(0, -6);

      const points = entry.slice(1).map((pt) => ({
        x: typeof pt[1] === "number" ? pt[1] : 0,
        y: typeof pt[2] === "string" ? parseFloat(pt[2]) : 0,
      }));

      const color = getRandomColor();
      return {
        label: dateStr,
        data: points,
        borderColor: color,
        backgroundColor: color,
        tension: 0.2,
        yAxisID: 'yDepth',
        pointRadius: 2,
      };
    }),
  }), [data]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    // Animating dozens of series on every update was a large part of the lag.
    animation: false,
    normalized: true,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false
    },
    scales: {
      x: {
        type: "linear",
        min: -1.0,
        max: 1.0,
        title: {
          display: true,
          text: "Deflection (inches)"
        },
        grid: {
          display: false
        }
      },
      yDepth: {
        position: "left",
        max: 0,
        ticks: {
          stepSize: 5,
          callback: (val) => `${val}`
        },
        title: {
          display: true,
          text: "Depth (feet)"
        },
        grid: {
          drawOnChartArea: true,
          color: "#e0e0e0"
        }
      },
      yPressure: {
        position: "right",
        min: -rounded(lastdept) + parseFloat(66.5),
        max: 66.5,
        ticks: {
          stepSize: 5,
          callback: (val) => val.toFixed(1)
        },
        title: {
          display: true,
          text: "Elevation (feet)"
        },
        grid: {
          drawOnChartArea: true
        }
      }
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 5,
          boxHeight: 5,
          padding: 5,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        enabled: true
      }
    }
  }), [lastdept]);

  return (
    <div className="graph" style={{ height: '800px', overflowY: 'auto' }}>
      <Line options={chartOptions} data={chartData} />
    </div>
  );
};

// The page re-renders for unrelated reasons; without this the chart rebuilt
// each time even when its readings had not changed.
export default React.memo(ISGraphChartjs);
